import { useState, useEffect } from "react";
import Navigation from "./components/Navigation";
import Banner from "./components/Banner";
import Products from "./components/Products";
import AboutUs from "./components/AboutUs";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import AIChat from "./components/AIChat";
import { useVisitorTracker } from "@/hooks/useVisitorTracker";
import { useHreflang } from "@/hooks/useHreflang";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

interface HomeJsonLdProps {
  lang: "zh" | "en";
}

function HomeJsonLd({ lang }: HomeJsonLdProps) {
  useEffect(() => {
    const orgSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "星核动力科技有限公司",
      alternateName: "Xinghe Propulsion Technology",
      url: SITE_URL,
      logo: "/local-images/c1919b7175e0.png",
      description:
        "星核动力科技有限公司专注于航空发动机研发制造、无人机动力系统及精密制造服务，提供涡扇、涡轴、电动推进、混合动力等航空推进解决方案，通过CCAR-33适航认证。",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        availableLanguage: ["Chinese", "English"],
      },
      sameAs: [],
    };

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "星核动力科技",
      url: SITE_URL,
      description: "航空发动机、无人机动力、精密制造技术服务",
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${SITE_URL}/products?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    };

    // Bilingual WebPage schema — tells Google each language version separately
    const webPageZh = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/?lang=zh`,
      url: `${SITE_URL}/?lang=zh`,
      name: "星核动力 | 航空发动机·无人机动力·精密制造",
      description:
        "星核动力科技有限公司专注于航空发动机研发制造、无人机动力系统及精密制造服务，提供涡扇、涡轴、电动推进、混合动力等航空推进解决方案。",
      inLanguage: "zh-CN",
      isPartOf: { "@id": SITE_URL },
    };

    const webPageEn = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/?lang=en`,
      url: `${SITE_URL}/?lang=en`,
      name: "Xinghe Propulsion | Aero Engines · UAV Power · Precision Manufacturing",
      description:
        "Xinghe Propulsion Technology specializes in aircraft engine R&D, UAV propulsion systems, and precision manufacturing, offering turbofan, turboshaft, electric and hybrid aerospace propulsion solutions.",
      inLanguage: "en",
      isPartOf: { "@id": SITE_URL },
    };

    const scripts: HTMLScriptElement[] = [];

    [orgSchema, websiteSchema, webPageZh, webPageEn].forEach((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(schema);
      script.setAttribute("data-schema", "home");
      document.head.appendChild(script);
      scripts.push(script);
    });

    // Update <title> and <meta name="description"> based on active language
    const titleEl = document.querySelector("title");
    const descEl = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (lang === "en") {
      if (titleEl) titleEl.textContent = "Xinghe Propulsion | Aero Engines · UAV Power · Precision Manufacturing";
      if (descEl)
        descEl.content =
          "Xinghe Propulsion Technology specializes in aircraft engine R&D, UAV propulsion systems, and precision manufacturing, offering turbofan, turboshaft, electric and hybrid aerospace propulsion solutions with CCAR-33 certification.";
    } else {
      if (titleEl) titleEl.textContent = "星核动力 | 航空发动机·无人机动力·精密制造";
      if (descEl)
        descEl.content =
          "星核动力科技有限公司专注于航空发动机研发制造、无人机动力系统及精密制造服务，提供涡扇、涡轴、电动推进、混合动力等航空推进解决方案，通过CCAR-33适航认证。";
    }

    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, [lang]);

  return null;
}

export default function Home() {
  const [lang, setLang] = useState<"zh" | "en">("zh");
  useVisitorTracker();
  useHreflang(lang);

  const toggleLang = () => setLang((prev) => (prev === "zh" ? "en" : "zh"));

  return (
    <div className="min-h-screen bg-xh-bg font-sans">
      <HomeJsonLd lang={lang} />
      <Navigation lang={lang} onLangToggle={toggleLang} />
      <main>
        <Banner lang={lang} />
        <Products lang={lang} />
        <AboutUs lang={lang} />
        <Contact lang={lang} />
      </main>
      <Footer lang={lang} />
      <AIChat lang={lang} />
    </div>
  );
}
